/** @jsxImportSource @emotion/react */

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Typography from '@mui/material/Typography'
import { isEqual } from 'lodash'
import { SyntheticEvent, useCallback, useState } from 'react'
import { ReactSortable } from 'react-sortablejs'
import { SORTABLE_LIST_ANIMATION } from 'constants/consts'
import { findOneQuestionPassageBank_findOneQuestionPassageBank_questionPassages as QuestionPassage } from '__generatedTypes__/findOneQuestionPassageBank'
import { ReactComponent as EmptyListSvg } from 'components/QuestionAccordion/images/empty.svg'
import * as classes from 'components/QuestionAccordion/styles'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { AccordionSummaryContent } from './components/AccordionSummaryContent'

export type QuestionPassageAccordionProps = {
  questionPassages: QuestionPassage[]
  onSort?: (questions: QuestionPassage[]) => void
  onDeleteClick?: (question: QuestionPassage, index: number) => void
  onQuestionPassageUpdated?: (questionPassageUpd: QuestionPassage, index: number) => void
}

export function QuestionPassageAccordion({
  questionPassages,
  onSort,
  onDeleteClick,
  onQuestionPassageUpdated,
}: QuestionPassageAccordionProps) {
  const { getLink } = useLink()
  const sortedQuestionPassages = questionPassages.map((questionPassage) => ({
    id: questionPassage.id,
    questionPassage,
  }))
  const [expanded, setExpanded] = useState<string | null>(questionPassages[0]?.id || null)

  const handleSort = useCallback(
    (sortedQuestionPassagesNew: Array<{ id: string; questionPassage: QuestionPassage }>) => {
      const newQuestionPassages = sortedQuestionPassagesNew.map(({ questionPassage }) => questionPassage)

      if (isEqual(questionPassages, newQuestionPassages)) {
        return
      }

      onSort?.(newQuestionPassages)
    },
    [questionPassages]
  )

  const handleDeleteClick = (e: SyntheticEvent<SVGSVGElement>, questionPassage: QuestionPassage, index: number) => {
    e.stopPropagation()
    onDeleteClick?.(questionPassage, index)
  }

  const handleEditClick = (e: SyntheticEvent<SVGSVGElement>, questionPassage: QuestionPassage) => {
    e.stopPropagation()
    const editLink = getLink(cmsRoutes.CARS_QUESTION_EDIT, { questionPassageId: questionPassage.id })
    window.open(editLink, '_blank')
  }

  const handleExpandClick = (questionPassageId: string) => () => {
    setExpanded((prevState) => (prevState === questionPassageId ? null : questionPassageId))
  }

  if (!questionPassages.length) {
    return (
      <div css={[classes.container, classes.emptyContainer]}>
        <EmptyListSvg />
        <div>No questions at the moment</div>
      </div>
    )
  }

  return (
    <>
      <ReactSortable
        list={sortedQuestionPassages}
        setList={handleSort}
        animation={SORTABLE_LIST_ANIMATION}
        css={classes.container}
        data-testid="question-passages-list"
      >
        {sortedQuestionPassages.map(({ questionPassage }, index) => (
          <Accordion
            key={questionPassage.id}
            expanded={expanded === questionPassage.id}
            onChange={handleExpandClick(questionPassage.id)}
          >
            <AccordionSummary
              css={[classes.accordionSummary, expanded === questionPassage.id && classes.accordionSummaryOpen]}
            >
              <AccordionSummaryContent
                index={index}
                expanded={expanded}
                questionPassage={questionPassage}
                onSuccessfulEdit={onQuestionPassageUpdated}
                onEditClick={handleEditClick}
                onDeleteClick={handleDeleteClick}
              />
            </AccordionSummary>
            <AccordionDetails css={classes.accordionDetails}>
              <div css={classes.questionTitle}>
                <Typography variant="body2">Passage</Typography>
                <div
                  css={classes.itemContent}
                  dangerouslySetInnerHTML={{ __html: questionPassage.content }}
                  data-testid="question-passage-text"
                />
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
      </ReactSortable>
    </>
  )
}

export default QuestionPassageAccordion
